import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatMenuModule } from '@angular/material/menu';
import { MatSelect, MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { BehaviorSubject, Observable } from 'rxjs';
import { distinctUntilChanged, filter, map, startWith } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateModule } from '@ngx-translate/core';
import { AuthService } from '@nimbus-assistant/services/auth.service';
import { PresenceService } from '@nimbus-assistant/services/presence.service';
import { MessageService } from '@nimbus-assistant/services/message.service';
import { SettingsService } from '@nimbus-assistant/services/settings.service';
import { ResponsibilityProfileService } from '@nimbus-assistant/services/responsibility-profile.service';
import { OpenInBrowserMessage } from '@nimbus-assistant/models/web-view-messages/open-in-browser-message';
import { UserSelectableState, UserStateService } from '@nimbus-assistant/services/user-state.service';
import { TaskOutboundService } from '@nimbus-assistant/services/task-outbound.service';
import { TaskToastService } from '@nimbus-assistant/services/task-toast.service';
import { AppRoutes } from '@nimbus-assistant/enums/app-routes';
import { Features } from '@nimbus-assistant/enums/features';
import { RoutingService } from '@nimbus-assistant/services/routing.service';
import { presenceMap } from '@nimbus-global/features/ui/models/presence-map.model';
import { SharedUIModule } from '@nimbus-library/features/shared/shared.ui.module';
import { TranslationKeys } from '@nimbus-library/features/localization/models/enums/translation-keys.enum';
import { ICONS } from '@nimbus-library/features/shared/resources/icons';
import { EnvironmentConfigurationService } from '@nimbus-library/features/configuration';

@UntilDestroy()
@Component({
  standalone: true,
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    TranslateModule,
    MatCardModule,
    MatMenuModule,
    MatSelectModule,
    MatButtonModule,
    SharedUIModule,
  ],
})
export class HeaderComponent {
  @ViewChild('matSelect') public profileSelect: MatSelect;

  private hideHeaderURLs = [AppRoutes.info, AppRoutes.settings, AppRoutes.notAvailableReason, AppRoutes.outbound];

  public get profile(): string {
    return this.profileService.profile$.value ? this.profileService.profile$.value.id : '';
  }
  public set profile(value: string) {
    this.profileService.changeResponsibilityProfile(value);
  }

  public ICONS = ICONS;
  public isInfo$ = new BehaviorSubject<boolean>(false);
  public hideHeader$ = new BehaviorSubject<boolean>(false);

  public profileChangeDisabled$: Observable<boolean>;

  public openLogFolderEnabled = false;

  public displayOutboundIcon = false;
  public disableOutboundIcon = false;

  public currentUrl: string = '';
  public isExtendingTeamsToast: boolean;

  public TranslationKeys = TranslationKeys;
  public presenceMap = presenceMap;

  public constructor(
    public readonly authService: AuthService,
    public readonly presenceService: PresenceService,
    public readonly settingsService: SettingsService,
    public readonly profileService: ResponsibilityProfileService,
    private readonly userStateService: UserStateService,
    private readonly taskToastService: TaskToastService,
    private readonly configuration: EnvironmentConfigurationService,
    private readonly router: Router,
    private readonly routingService: RoutingService,
    private readonly messageService: MessageService,
    private readonly cd: ChangeDetectorRef,
    private readonly taskOutboundService: TaskOutboundService,
  ) {
    this.settingsService.settings$.pipe(untilDestroyed(this)).subscribe((settings) => {
      this.displayOutboundIcon = settings.outboundServices?.length > 0;
      this.cd.markForCheck();
    });

    this.profileChangeDisabled$ = this.userStateService.selectableState$.pipe(
      map((x) => x === UserSelectableState.noConnectionError),
      distinctUntilChanged(),
      untilDestroyed(this),
    );

    this.taskOutboundService.isOutboundDisabled$.pipe(untilDestroyed(this)).subscribe((disabled) => {
      this.disableOutboundIcon = disabled;
      this.cd.markForCheck();
    });

    // The app shell may render the header after the initial NavigationEnd (translations gate), so start with the current URL.
    this.router.events
      .pipe(
        untilDestroyed(this),
        filter((event) => event instanceof NavigationEnd),
        map((event) => (event as NavigationEnd).url),
        startWith(this.router.url),
      )
      .subscribe((url) => {
        this.isInfo$.next(url.endsWith(AppRoutes.info));
        this.hideHeader$.next(!!this.hideHeaderURLs.find((x) => url.endsWith(x)));
        if (!url.endsWith(AppRoutes.settings) && this.currentUrl.endsWith(AppRoutes.settings)) {
          this.messageService.sendRepositionCurrentWindow();
        }

        this.currentUrl = url;
        this.updateHideHeader();
      });

    this.taskToastService.isExtendingTeamsToast$.pipe(untilDestroyed(this)).subscribe((isExtending) => {
      this.isExtendingTeamsToast = isExtending;
      this.updateHideHeader();
      this.cd.markForCheck();
    });

    this.settingsService.desktopVersion$.pipe(untilDestroyed(this)).subscribe((_) => {
      this.openLogFolderEnabled = this.settingsService.supportsFeature(Features.OpenLogFolder);
      this.cd.markForCheck();
    });
  }

  public openNimbus(): void {
    const message = new OpenInBrowserMessage();
    const environment = this.configuration.configurationSettings.mainLocation;
    message.url = `https://portal.${environment}`;
    MessageService.SendOpenInBrowser(message);
  }

  public openLogFolder(): void {
    MessageService.SendOpenLogFolderMessage();
  }

  public back(): void {
    this.routingService.goToDefaultPage();
  }

  public quit(): void {
    MessageService.SendQuit();
  }

  public selectClick(): void {
    if (this.profileSelect.panelOpen) {
      this.profileService.updateProfiles();
    }
  }

  private updateHideHeader(): void {
    this.hideHeader$.next(!!this.hideHeaderURLs.find((x) => this.currentUrl.endsWith(x)) || this.isExtendingTeamsToast);
  }
}
